import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Btn } from "@/admin/AdminLayout";

function CatRow({ c, onDone }: { c: any; onDone: () => void }) {
  const [name, setName] = useState(c.name);
  const tid = c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const rename = async () => {
    try { const r = await api.post("/admin-x/blog/categories/rename", { old: c.name, new: name }); toast(r.data.merged ? `Merged into "${name}"` : `Renamed to "${name}" on ${c.posts} article${c.posts === 1 ? "" : "s"}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove "${c.name}"? It will be taken off ${c.posts} article(s); articles left without a category move to Journal.`)) return;
    try { await api.delete(`/admin-x/blog/categories/${encodeURIComponent(c.name)}`); toast(`"${c.name}" removed`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-3 py-2 border-b border-neutral-100" data-testid={`cat-row-${tid}`}>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} className="flex-1 border border-neutral-300 px-3 py-2 text-sm" data-testid={`cat-name-${tid}`} />
      <span className="text-xs text-neutral-500 w-20">{c.posts} article{c.posts === 1 ? "" : "s"}</span>
      <Btn ghost onClick={rename} disabled={!name.trim() || name.trim() === c.name} data-testid={`cat-rename-${tid}`}>Rename</Btn>
      <Btn ghost onClick={remove} data-testid={`cat-delete-${tid}`}>Remove</Btn>
    </div>
  );
}

function AddCategory({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const add = async () => {
    try { await api.post("/admin-x/blog/categories", { name }); toast(`Category "${name.trim()}" added`); setName(""); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-3 pt-4 border-t border-neutral-200" data-testid="cat-add-row">
      <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) add(); }} maxLength={40}
        placeholder="New category name, e.g. Bridal" className="flex-1 border border-neutral-300 px-3 py-2 text-sm" data-testid="cat-add-input" />
      <Btn onClick={add} disabled={!name.trim()} data-testid="cat-add-button">Add category</Btn>
    </div>
  );
}

export function JournalTools({ onChange }: { onChange: () => void }) {
  const [cats, setCats] = useState<any[] | null>(null);
  const load = () => api.get("/admin-x/blog/categories").then((r) => setCats(r.data)).catch((e) => toast.error(errMsg(e)));
  const done = () => { load(); onChange(); };
  const report = async () => {
    try { const r = await api.post("/admin-x/blog/report"); toast(r.data.sent ? `Journal report emailed to ${r.data.to}` : "Report saved, but the email could not be sent"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Btn ghost onClick={load} data-testid="manage-categories-open">Manage categories</Btn>
      <Btn ghost onClick={report} data-testid="journal-report-send">Email report now</Btn>
      <Dialog open={!!cats} onOpenChange={(v) => { if (!v) setCats(null); }}>
        <DialogContent className="bg-white max-w-xl" data-testid="manage-categories-dialog">
          <DialogTitle className="font-display text-2xl">Blog categories</DialogTitle>
          <p className="text-sm text-neutral-600">Rename a category to update every article using it (renaming to an existing name merges them). Removing takes it off all articles.</p>
          <div className="max-h-[60vh] overflow-y-auto">{cats?.map((c) => <CatRow key={c.name} c={c} onDone={done} />)}</div>
          <AddCategory onDone={done} />
        </DialogContent>
      </Dialog>
    </>
  );
}
